import { cva, type VariantProps } from 'class-variance-authority';
import cn from 'classnames';
import { forwardRef, type JSX } from 'react';
import {
  Card as BaseCard,
  BaseCardProps,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './card';

export type CardStylingProps = VariantProps<typeof cardVariants>;

interface CardProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    CardStylingProps,
    BaseCardProps {}

export const cardVariants = cva('', {
  variants: {
    radii: {
      none: 'rounded-none',
      xs: 'rounded-xs',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
    },
    padding: {
      none: 'p-0',
      xs: 'p-1',
      sm: 'p-2',
      md: 'p-4',
      lg: 'p-6',
      xl: 'p-8',
    },
    shadow: {
      none: '',
      xs: 'shadow-xs',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
    },
    hoverable: {
      true: 'transition-shadow duration-300 ease-in hover:shadow-md',
      false: '',
    },
    bordered: {
      true: 'border border-solid border-card',
      false: '',
    },
  },
  defaultVariants: {
    radii: 'md',
    padding: 'md',
    shadow: 'md',
    hoverable: false,
    bordered: false,
  },
});

/**
 * Organize and present content in structured containers with customizable padding, borders, and interactive states.
 *
 * Often with a body and an optional footer.
 * Cards are ideal for showcasing information compactly,
 * making them suitable for use in dashboards, lists, and various content panels.
 */
const Card = Object.assign(
  forwardRef(
    (
      {
        radii,
        hoverable,
        bordered,
        padding,
        shadow,
        className,
        ...props
      }: CardProps,
      ref: React.Ref<HTMLDivElement>,
    ): JSX.Element => {
      return (
        <BaseCard
          ref={ref}
          className={cn(
            cardVariants({
              radii,
              hoverable,
              bordered,
              padding,
              shadow,
            }),
            className,
          )}
          {...props}
        />
      );
    },
  ),
  {
    Header: CardHeader,
    Content: CardContent,
    Footer: CardFooter,
    Title: CardTitle,
    Description: CardDescription,
  },
);

export default Card;
